import CardSystem from '../CardSystem';
import ITask from '../../../Utility/Task/ITask';
import Point from '../../../Utility/Point';
import TNTModifier from 'Prototype/Level/Card/Modifier/TNT';
import { AwaitIterators, ConcatIterators, PlayAnimation } from 'System/Animation/AnimationHelper';
import { DragonbonesDefault } from 'System/Animation/Clip';
import TransformComponent from 'Component/Transform';
import CardModifierComponent from 'Component/Level/CardModifier';
import { MathHelper } from 'Utility/MathHelper';
import PIXIRenderer from 'System/Render/PIXIRenderer';
import { MainAnim } from 'System/Animation/Clip/MainAnim';
import { delay } from 'Utility/Task/TaskQueue';
import BaseCard from 'Prototype/Level/Card/BaseCard';
import { RAD_TO_DEG } from 'pixi.js';
import CardComponent from 'Component/Level/Card';
import { UIActions } from 'State/UI/UIActions';
import { PopupID } from 'State/UI/PopupID';
import Game from 'Game';
import { LevelFailReason } from 'State/Game/GameActions';
import { PlaySound } from 'System/Audio/AudioHelper';

export default class TNTModifierLevelFailTask implements ITask<CardSystem> {
    private modifier: TNTModifier;

    constructor(modifier: TNTModifier) {
        this.modifier = modifier;
    }

    public *execute(system: CardSystem): IterableIterator<number> {
        //const isLastTask = system.taskQueue.some((task, index) => (this === task && index === system.taskQueue.getTaskCount() - 1));
        const modifierComponent = this.modifier.getComponent(CardModifierComponent);
        const targetCards = [
            ...system.getCardsInPlay().filter(card => card.id !== modifierComponent.cardID),
            ...system.getCardsInStack(),
            ...system.getCardsFolded(),
        ];
        const explodingCard = system.getCardByID(modifierComponent.cardID);
        const explosionEpicenter = explodingCard.getComponent(TransformComponent).position.clone();

        [explodingCard, ...targetCards].forEach((card) => card.getComponent(CardComponent).animating = true);

        yield* AwaitIterators([
            this.getExplosionIterables(system, explodingCard),
            this.getShockwaveIterables(targetCards, explosionEpicenter),
        ]);

        // Prevent any tasks after this from playing
        while (true) {
            yield;
        }
    }

    public *getExplosionIterables(system: CardSystem, card: BaseCard): IterableIterator<number> {
        const bombArmature = this.modifier.getLayout().tnt_modifier_armature;
        system.animator.addChild(bombArmature);

        const explosionAnimation = PlayAnimation(new DragonbonesDefault(bombArmature, {
            animationName: "tnt_explosion",
            playTimes: 1
        }));

        const hideCardAnimation = PlayAnimation(new MainAnim(card, {
            alpha: 0,
            duration: 50
        }));
        
        PlaySound('tnt_explosion');

        yield* delay(1200);

        yield* AwaitIterators([
            explosionAnimation,
            ConcatIterators([
                delay(200),
                hideCardAnimation
            ])
        ]);
        const state = Game.store.getState();
        UIActions.showPopup(PopupID.LEVEL_POPUP,
            {
                levelNumber: state.levelState.levelNumber,
                selectedPreBoosters: state.levelState.preBoosters.filter(
                    (booster) => state.playerState.inventory[booster] > 0,
                ),
                failReason: LevelFailReason.TNT_EXPLODED,
            }
        )
    }

    public *getShockwaveIterables(targetCards: BaseCard[], epicenter: Point): IterableIterator<number> {
        const screenSize = Math.max(PIXIRenderer.renderer.width, PIXIRenderer.renderer.height);
        const targetCardShockwaveIterables: IterableIterator<number>[] = [];

        for (let i = 0; i < targetCards.length; i++) {
            const card = targetCards[i];
            const cardTransform = card.getComponent(TransformComponent);
            const cardStartPosition = cardTransform.position.clone();
            const endPoint = Point.fromPointData(MathHelper.getPointOnLine(epicenter, cardStartPosition, screenSize));
            const rotation = RAD_TO_DEG * MathHelper.getAngleOfLine(epicenter, cardStartPosition) || 15
            const transformAnimation = PlayAnimation(new MainAnim(card, {
                x: endPoint.x,
                y: endPoint.y,
                rotation,
                duration: 1500
            }));
            targetCardShockwaveIterables.push(transformAnimation)
        }

        yield* ConcatIterators([
            delay(1400),
            AwaitIterators(targetCardShockwaveIterables)
        ]);
    }
}
